/**
 * Minimum-zoom hints for overlays in a Leaflet layers control.
 *
 * Several overlays only load their data once the map is zoomed in enough (cadastral parcels,
 * zoning-plan areas). Without a hint, switching such an overlay on at a low zoom looks broken —
 * the checkbox is ticked and nothing appears. Registered overlays get a "min. přiblížení N" suffix
 * in the control and their row is dimmed while the map stays below that zoom.
 */
import L from 'leaflet';

const SUFFIX_CLASS = 'map-layer-min-zoom';
const UNAVAILABLE_CLASS = 'map-layer--unavailable';

interface ControlRegistry {
    readonly map: L.Map;
    readonly minZooms: Map<number, number>;
    readonly refresh: () => void;
    readonly patchedUpdate: (() => unknown) | null;
    readonly originalUpdate: (() => unknown) | null;
}

interface LayersControlInternals {
    _update?: () => unknown;
    _overlaysList?: HTMLElement;
}

const registries: Map<L.Control.Layers, ControlRegistry> = new Map();

/**
 * Announce that an overlay only shows data from the given zoom level up.
 */
export function registerMinZoom(
    map: L.Map, layersControl: L.Control.Layers, layer: L.Layer, minZoom: number,
): void {
    const registry = registryFor(map, layersControl);
    registry.minZooms.set(L.Util.stamp(layer), minZoom);
    registry.refresh();
}

/**
 * Drop the overlays a component registered on the control; call when the component detaches.
 */
export function unregisterMinZooms(layersControl: L.Control.Layers, layers: readonly L.Layer[]): void {
    const registry = registries.get(layersControl);

    if (!registry) {
        return;
    }

    for (const layer of layers) {
        registry.minZooms.delete(L.Util.stamp(layer));
    }

    if (registry.minZooms.size > 0) {
        registry.refresh();
        return;
    }

    const internals = layersControl as unknown as LayersControlInternals;

    // Another component may have patched _update on top of ours — leave its patch alone in that case.
    if (registry.originalUpdate && internals._update === registry.patchedUpdate) {
        internals._update = registry.originalUpdate;
    }

    registry.map.off('zoomend', registry.refresh);
    registries.delete(layersControl);
}

function registryFor(map: L.Map, layersControl: L.Control.Layers): ControlRegistry {
    const existing = registries.get(layersControl);

    if (existing) {
        return existing;
    }

    const minZooms = new Map<number, number>();
    const refresh = (): void => decorateRows(layersControl, map, minZooms);

    // The control rebuilds its list on every change, so the suffixes have to be re-applied after each.
    const internals = layersControl as unknown as LayersControlInternals;
    const originalUpdate = internals._update ?? null;
    const patchedUpdate = originalUpdate === null
        ? null
        : (): unknown => {
            const result = originalUpdate.call(layersControl);
            refresh();
            return result;
        };

    if (patchedUpdate) {
        internals._update = patchedUpdate;
    }

    map.on('zoomend', refresh);

    const registry: ControlRegistry = {map, minZooms, refresh, patchedUpdate, originalUpdate};
    registries.set(layersControl, registry);

    return registry;
}

function decorateRows(
    layersControl: L.Control.Layers, map: L.Map, minZooms: Map<number, number>,
): void {
    const overlaysList = (layersControl as unknown as LayersControlInternals)._overlaysList;

    if (!overlaysList) {
        return;
    }

    const zoom = map.getZoom();

    overlaysList.querySelectorAll('label').forEach((label) => {
        const input = label.querySelector('input') as unknown as {layerId?: number} | null;
        const minZoom = input?.layerId !== undefined ? minZooms.get(input.layerId) : undefined;

        if (minZoom === undefined) {
            return;
        }

        if (!label.querySelector(`.${SUFFIX_CLASS}`)) {
            const suffix = document.createElement('span');
            suffix.className = SUFFIX_CLASS;
            suffix.textContent = `min. přiblížení ${minZoom}`;
            label.appendChild(suffix);
        }

        label.classList.toggle(UNAVAILABLE_CLASS, zoom < minZoom);
    });
}
